import Konva from 'konva';
import React from 'react';
import { Group } from 'react-konva';
import { SpotShape } from '../../../../types';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import { getAbsolutePositionOfShape } from '../../helpers/getAbsolutePositionOfShape';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { ShapeCommonProps } from '../../types';
import { useShape } from '../ShapeContext/useShape';
import { SpotItem } from './SpotShapeItem';
import { SpotItemSVG } from './SpotShapeItemSVG';
import { getOrientationLayout } from './helpers';

export interface SpotShapeComponentProps extends ShapeCommonProps {
  renderMode?: 'konva' | 'svg';
}

export const SpotShapeComponent = React.memo(
  React.forwardRef<React.ElementRef<typeof Group>, SpotShapeComponentProps>(
    (
      {
        onDragStart,
        onDragMove,
        onDragEnd,
        onTransformEnd,
        onContextMenu,
        onMouseEnter,
        onMouseLeave,
        highlightShape,
        highlightShapeData,
        onClick,
        onTap,
        draggable,
        renderMode = 'konva'
      },
      ref
    ) => {
      const { shape: spotShape } = useShape();
      const { isViewMode, interactiveZones } = useMapCanvas();
      const shape = spotShape as SpotShape;
      const interactiveZone =
        isViewMode && shape.resource_id
          ? interactiveZones?.[shape.resource_id]
          : undefined;

      const handleCircleClick = (
        event: Konva.KonvaEventObject<PointerEvent>,
        resourceId?: string
      ) => {
        if (!isViewMode || shape.isInactive) return;
        event.cancelBubble = true;
        if (isViewMode && resourceId) {
          const interactiveZone = interactiveZones?.[resourceId];
          if (interactiveZone) {
            const stage = getWindowKonvaObject().stages[0];
            if (!stage) return;

            const absPos = getAbsolutePositionOfShape(`${shape.id}`, stage);

            interactiveZone.onAction?.({
              x: absPos?.x || 0,
              y: absPos?.y || 0
            });
          }
        }
      };

      // Memoize the layout calculations
      const layouts = React.useMemo(() => {
        return getOrientationLayout(
          shape.spotAppearance || shape.spotType,
          shape.orientation,
          shape.labelPlacement,
          shape.labelOrientation || 'auto',
          0,
          0,
          shape.spacing || 0,
          shape.rowSpacing || 0
        );
      }, [
        shape.orientation,
        shape.labelPlacement,
        shape.spacing,
        shape.rowSpacing,
        shape.spotType,
        shape.spotAppearance,
        shape.labelOrientation
      ]);

      const isVertical =
        shape.orientation === 'left' || shape.orientation === 'right';

      // If renderMode is SVG, return SVG components
      if (renderMode === 'svg') {
        return (
          <SpotItemSVG
            item={shape}
            layout={layouts}
            interactiveZone={interactiveZone}
            isHighlighted={highlightShapeData?.targetId === shape.id}
          />
        );
      }

      // Default Konva rendering
      return (
        <Group
          draggable={draggable}
          id={`shape-${shape.id}`}
          x={shape.x}
          y={shape.y}
          rotation={shape.rotation}
          width={shape.width}
          height={shape.height}
          onClick={onClick}
          onTap={onTap}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onTransformEnd={onTransformEnd}
          onContextMenu={onContextMenu}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={ref}
        >
          <SpotItem
            key={shape.id}
            item={shape}
            layout={layouts}
            onSpotClick={(e) => {
              handleCircleClick(e, shape.resource_id);
            }}
            interactiveZone={interactiveZone}
            isHighlighted={highlightShapeData?.targetId === shape.id}
            onShapeHighlight={
              highlightShape
                ? () => {
                    highlightShape({ shape, targetId: shape.id });
                  }
                : undefined
            }
          />
        </Group>
      );
    }
  )
);

SpotShapeComponent.displayName = 'SpotShapeComponent';
